import React, { useState, useRef, useEffect } from 'react';
import { Dropdown } from './Dropdown';
import { Checkbox } from './Checkbox';
import { DateRangePicker } from '../../../_shared/DateRangePicker';
import { LocationIcon, CalendarIcon, GuestsIcon, MoreFiltersIcon } from './Icons';
import { MinusPlus } from './MinusPlus';
import { dispatchOceanSearch, getSearchParamsFromUrl, isOnSearchResultsPage, OceanLockedScope, OceanSearchParams } from '../../../_shared/oceanSearchContext';
import { useDebouncedCallback } from '../../../_shared/useDebouncedCallback';
import { useFlipPlacement } from './useFlipPlacement';

// Mirrors the field blocks under jrx/blox/booking-bar-field-* -- each row's
// config is read off that block's saved data-field-type/data-* markers by
// index.tsx's readRowsConfig(), not decided in this component. This is the
// single source of truth for what a row entry can be.
export type FieldConfig =
	| { type: 'dates' }
	| { type: 'guests'; min: number; max: number }
	| { type: 'location' }
	| { type: 'feature'; slug: string }
	| { type: 'submit'; buttonText: string }
	// A container, not a leaf: `fields` are the blocks nested inside the
	// block's own InnerBlocks, which render in its dropdown panel.
	| { type: 'more-filters'; buttonText: string; fields: FieldConfig[] };

interface BookingBarFeature
	{
	value: string;
	label: string;
	icon?: string | null;
	caveat?: string | null;
	shortName?: string | null;
	}

// One ocean/booking-bar-row block's worth of fields, plus the row's own
// editor-chosen alignment (see index.tsx's readRowsConfig() -- extracted
// from the row's saved `is-content-justification-*` class, since WP's block
// "layout" support only bakes that into the row's own wrapper element, not
// into anything a caller can otherwise read).
export interface RowConfig
	{
	fields: FieldConfig[];
	justifyContent?: string;
	}

interface BookingBarFormProps
	{
	rows: RowConfig[];
	backgroundOpacity?: number;
	locations: Array<{ value: string; label: string }>;
	lockedScope?: OceanLockedScope | null;
	initialLocation?: string;
	initialCheckIn?: string;
	initialCheckOut?: string;
	initialGuests?: number;
	initialDateFlex?: 'exact' | 'pm3' | 'pm7';
	features?: BookingBarFeature[];
	initialFeatures?: string[];
	}

export const BookingBarForm = ({
	rows,
	backgroundOpacity = 0.62,
	locations,
	lockedScope = null,
	initialLocation = '',
	initialCheckIn = '',
	initialCheckOut = '',
	initialGuests = 1,
	initialDateFlex = 'exact',
	features = [],
	initialFeatures = [],
	}: BookingBarFormProps) =>
	{
	// The locked location isn't user-editable, so seed it in but there's no
	// control to change it -- see the 'location' case below, which skips
	// rendering entirely when locked.
	const [location, setLocation] = useState(lockedScope?.taxonomy === 'property-location' ? lockedScope.term : initialLocation);
	const [checkIn, setCheckIn] = useState(initialCheckIn);
	const [checkOut, setCheckOut] = useState(initialCheckOut);
	const [dateFlex, setDateFlex] = useState<'exact' | 'pm3' | 'pm7'>(initialDateFlex);
	const [guests, setGuests] = useState(initialGuests);
	const [selectedFeatures, setSelectedFeatures] = useState<string[]>(initialFeatures);
	const [showLocationDropdown, setShowLocationDropdown] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	// The 'more-filters' panel -- see moreFiltersRowIndex below. Always starts
	// closed, like every other dropdown in the bar. An earlier version opened
	// it when the visitor arrived with feature filters already applied, so the
	// ticked boxes weren't hidden; that made sense while these were an inline
	// row, but a panel that pops itself open over the page on load doesn't.
	// See the note on renderMoreFiltersField about surfacing the active count
	// on the trigger instead.
	const [showMoreFilters, setShowMoreFilters] = useState(false);
	const datesFieldRef = useRef<HTMLDivElement>(null);
	const locationFieldRef = useRef<HTMLDivElement>(null);
	const moreFiltersFieldRef = useRef<HTMLDivElement>(null);

	// Which way each open panel hangs off its field. Called unconditionally
	// here rather than inside the render helpers below -- those are skipped
	// entirely when the editor didn't place that field, which would make the
	// hook calls conditional. Each returns 'down' while its panel is closed,
	// so an absent field simply costs nothing.
	const datesPlacement = useFlipPlacement(datesFieldRef, showDatePicker);
	const locationPlacement = useFlipPlacement(locationFieldRef, showLocationDropdown);
	const moreFiltersPlacement = useFlipPlacement(moreFiltersFieldRef, showMoreFilters);

	// The flip is expressed on the FIELD, not the panel -- the dropdown and
	// the calendar are separate components, and the field is the one element
	// both are positioned against, so one class covers both.
	const flipClass = (placement: 'down' | 'up') => (placement === 'up' ? ' is-flipped' : '');

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (datesFieldRef.current && !datesFieldRef.current.contains(e.target as Node)) {
				setShowDatePicker(false);
				}
			if (locationFieldRef.current && !locationFieldRef.current.contains(e.target as Node)) {
				setShowLocationDropdown(false);
				}
			if (moreFiltersFieldRef.current && !moreFiltersFieldRef.current.contains(e.target as Node)) {
				setShowMoreFilters(false);
				}
			};
		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
		}, []);

	const formatDate = (date: Date) => {
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const year = date.getFullYear();
		return `${month}/${day}/${year}`;
		};

	// Display only. checkIn/checkOut keep the MM/DD/YYYY form formatDate()
	// above produces -- that's what buildParams sends as check_in/check_out
	// and what the picker parses back with new Date(), so this deliberately
	// changes the label and nothing else.
	//
	// Two reasons it isn't just the raw value: "12/25/2026" is the widest
	// thing in the row by some margin (the bar's top row has no width to
	// spare, see booking-bar.scss's &__row:first-child), and read by a UK
	// visitor "01/02/2027" says 1 February, not 2 January.
	const DISPLAY_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

	const formatDateForDisplay = (value: string): string => {
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? value : DISPLAY_DATE_FORMAT.format(date);
		};

	// Collection/bedroom/bathroom filters live in a separate sidebar filters
	// component (not this block) -- whatever it last set on the URL is
	// carried through untouched rather than reset by this form's own
	// changes. `amenities` is the one filter both blocks can touch -- every
	// 'feature' field (however many the editor placed, in either row)
	// toggles into the same flat selectedFeatures array, joined here
	// regardless of how many discrete checkboxes contributed to it.
	// `overrides` lets a field that just changed supply its new value
	// immediately, rather than waiting a render for its own state to update.
	const buildParams = (overrides: Partial<OceanSearchParams> = {}): Partial<OceanSearchParams> => ({
		...getSearchParamsFromUrl(),
		location,
		check_in: checkIn,
		check_out: checkOut,
		guests: guests.toString(),
		date_flex: dateFlex,
		amenities: selectedFeatures.join(','),
		collection: lockedScope?.taxonomy === 'property-collection' ? lockedScope.term : '',
		...overrides,
		});

	// Shared debounced live-dispatch for every field below except dates
	// (its own explicit Apply, see renderDatesField) -- several quick
	// changes (guests +/- spam, ticking a few feature checkboxes) collapse
	// into one request fired after a pause, instead of one per change. Only
	// called at all when already on the search-results page -- elsewhere
	// (e.g. a booking-bar on the homepage) field changes stay local until
	// the form's own Search button navigates there, see handleSearch.
	const dispatchLive = useDebouncedCallback((params: Partial<OceanSearchParams>) => dispatchOceanSearch(params), 400);

	const handleFeatureToggle = (featureValue: string) => {
		const next = selectedFeatures.includes(featureValue)
			? selectedFeatures.filter((f) => f !== featureValue)
			: [...selectedFeatures, featureValue];
		setSelectedFeatures(next);
		if (isOnSearchResultsPage()) {
			dispatchLive(buildParams({ amenities: next.join(',') }));
			}
		};

	const handleGuestsChange = (value: number) => {
		setGuests(value);
		if (isOnSearchResultsPage()) {
			dispatchLive(buildParams({ guests: value.toString() }));
			}
		};

	const handleLocationChange = (value: string) => {
		setLocation(value);
		setShowLocationDropdown(false);
		if (isOnSearchResultsPage()) {
			dispatchLive(buildParams({ location: value }));
			}
		};

	// The date popover's own explicit Apply -- see DateRangePicker.tsx's
	// onApply prop. checkIn/checkOut/dateFlex are already current by the
	// time this fires (onSelectRange/onDateFlexChange keep them live for
	// the trigger-button preview as the user picks), so no override needed;
	// dispatched immediately, not debounced, since a click is already a
	// deliberate single commit.
	const handleApplyDates = () => {
		setShowDatePicker(false);
		if (isOnSearchResultsPage()) {
			dispatchOceanSearch(buildParams());
			}
		};

	const handleMoreFiltersToggle = () => {
		setShowMoreFilters((shown) => !shown);
		};

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();

		const params = buildParams();

		// Fields are already live when a property-results block is on this
		// page (see dispatchLive above) -- this submit is then just a manual
		// "dispatch now" no-op. Off that page (e.g. a booking-bar on the
		// homepage), it's the only thing that navigates to the site's
		// dedicated results page.
		if (!isOnSearchResultsPage() && window.oceanSearchData?.searchResultsPageUrl) {
			const urlParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (value) urlParams.set(key, value);
				});
			window.location.href = `${window.oceanSearchData.searchResultsPageUrl}?${urlParams}`;
			return;
			}

		dispatchOceanSearch(params);
		};

	const renderDatesField = (key: number) => (
		<div className={`booking-bar__field booking-bar__field--dates${flipClass(datesPlacement)}`} role="group" aria-label="Dates" ref={datesFieldRef} key={key}>
			<button
				type="button"
				className="booking-bar__field-trigger"
				onClick={() => setShowDatePicker(!showDatePicker)}
			>
				<CalendarIcon />
				<span>{checkIn ? formatDateForDisplay(checkIn) : 'Check in'}</span>
			</button>
			<span className="booking-bar__field-divider booking-bar__field-divider--inner" aria-hidden="true" />
			<button
				type="button"
				className="booking-bar__field-trigger"
				onClick={() => setShowDatePicker(!showDatePicker)}
			>
				<CalendarIcon />
				<span>{checkOut ? formatDateForDisplay(checkOut) : 'Check out'}</span>
			</button>
			{showDatePicker && (
				<DateRangePicker
					startDate={checkIn ? new Date(checkIn) : null}
					endDate={checkOut ? new Date(checkOut) : null}
					onSelectRange={(start, end) => {
						setCheckIn(start ? formatDate(start) : '');
						setCheckOut(end ? formatDate(end) : '');
						}}
					onClose={() => setShowDatePicker(false)}
					onApply={handleApplyDates}
					dateFlex={dateFlex}
					onDateFlexChange={setDateFlex}
				/>
			)}
		</div>
	);

	const renderGuestsField = (key: number, min: number, max: number) => (
		<div className="booking-bar__field booking-bar__field--guests" role="group" aria-label="Guests" key={key}>
			<span className="booking-bar__field-static">
				<GuestsIcon />
				<span>Guests</span>
			</span>
			<MinusPlus value={guests} onChange={handleGuestsChange} min={min} max={max} />
		</div>
	);

	const renderLocationField = (key: number) => {
		if (lockedScope?.taxonomy === 'property-location') {
			return null;
			}

		return (
			<div className={`booking-bar__field booking-bar__field--location${flipClass(locationPlacement)}`} role="group" aria-label="Location" ref={locationFieldRef} key={key}>
				<button
					type="button"
					className="booking-bar__field-trigger"
					onClick={() => setShowLocationDropdown(!showLocationDropdown)}
				>
					<LocationIcon />
					<span>{locations.find((loc) => loc.value === location)?.label || 'Location'}</span>
				</button>
				{showLocationDropdown && (
					<Dropdown className="booking-bar__dropdown--location">
						<ul className="booking-bar__dropdown-list">
							<li>
								<button
									type="button"
									className={`booking-bar__dropdown-option booking-bar__dropdown-option--reset ${location === '' ? 'is-selected' : ''}`}
									onClick={() => handleLocationChange('')}
								>
									Cornwall
								</button>
							</li>
							{locations.map((loc) => (
								<li key={loc.value}>
									<button
										type="button"
										className={`booking-bar__dropdown-option ${location === loc.value ? 'is-selected' : ''}`}
										onClick={() => handleLocationChange(loc.value)}
									>
										{loc.label}
									</button>
								</li>
							))}
						</ul>
					</Dropdown>
				)}
			</div>
		);
		};

	const renderFeatureField = (key: number, slug: string) => {
		const feature = features.find((f) => f.value === slug);
		if (!feature) {
			return null;
			}

		return (
			<Checkbox
				key={key}
				checked={selectedFeatures.includes(feature.value)}
				onChange={() => handleFeatureToggle(feature.value)}
				label={feature.shortName || feature.label}
			/>
		);
		};

	const renderSubmitField = (key: number, buttonText: string) => (
		<button type="submit" className="booking-bar__submit wp-element-button" key={key}>
			{buttonText}
		</button>
	);

	// Deliberately the same field/trigger/dropdown markup as Location rather
	// than a control of its own: in the design this sits in the row's rhythm
	// as one more cell (label + 20px icon, its own divider), and reusing the
	// set means the row dividers, the trigger's ellipsis-on-shrink, the mobile
	// two-column tiling, the click-outside close and -- the one that would be
	// genuinely fiddly to reproduce -- the panel's upward flip when the bar is
	// pinned to the bottom of the viewport all apply without restating any of
	// them.
	//
	// The panel's contents are this field's own nested blocks -- rendered with
	// the same renderField() as any other field, so a filter behaves
	// identically whether an editor put it in the panel or straight in a row.
	const renderMoreFiltersField = (key: number, buttonText: string, fields: FieldConfig[]) => (
		<div className={`booking-bar__field booking-bar__field--more-filters${flipClass(moreFiltersPlacement)}`} role="group" aria-label="More filters" key={key} ref={moreFiltersFieldRef}>
			<button
				type="button"
				className="booking-bar__field-trigger"
				aria-expanded={showMoreFilters}
				onClick={handleMoreFiltersToggle}
			>
				<span>{buttonText}</span>
				<MoreFiltersIcon />
			</button>
			{showMoreFilters && (
				<Dropdown className="booking-bar__dropdown--more-filters">
					<div className="booking-bar__dropdown-filters">
						{fields.map((field, fieldIndex) => renderField(field, fieldIndex))}
					</div>
				</Dropdown>
			)}
		</div>
	);

	const renderField = (field: FieldConfig, key: number) => {
		switch (field.type) {
			case 'dates':
				return renderDatesField(key);
			case 'guests':
				return renderGuestsField(key, field.min, field.max);
			case 'location':
				return renderLocationField(key);
			case 'feature':
				return renderFeatureField(key, field.slug);
			case 'submit':
				return renderSubmitField(key, field.buttonText);
			case 'more-filters':
				return renderMoreFiltersField(key, field.buttonText, field.fields);
			default:
				return null;
			}
		};

	return (
		<div className="booking-bar" style={{ '--booking-bar-bg-opacity': backgroundOpacity } as React.CSSProperties}>
			<form className="booking-bar__form" onSubmit={handleSearch}>
				{/*
				  * Empty rows are skipped, not rendered empty. ocean/booking-bar
				  * always has exactly two rows (templateLock="insert"), so a bar
				  * whose filters all live in the More filters panel leaves the
				  * second one with nothing in it -- and &__row:nth-child(2)'s
				  * divider would draw a stray rule under the bar.
				  */}
				{rows.filter((row) => row.fields.length > 0).map((row, rowIndex) => (
					<div
						className="booking-bar__row"
						key={rowIndex}
						style={row.justifyContent ? { justifyContent: row.justifyContent } : undefined}
					>
						{row.fields.map((field, fieldIndex) => renderField(field, fieldIndex))}
					</div>
				))}
			</form>
		</div>
	);
};
